import type { TranslationDictionary } from "./types";

export const en: TranslationDictionary = {
  // ── EmailGate ──
  "page.title": "Your Proposal | Woba",
  "emailGate.placeholder": "your@email.com",
  "emailGate.invalidEmail": "Please enter a valid email",
  "emailGate.blockedEmail": "Please enter a valid corporate or personal email",
  "emailGate.loading": "Loading...",
  "emailGate.notFound": "Proposal not found",
  "emailGate.loadError":
    "Could not load the proposal. Please check the link or try again.",
  "emailGate.retry": "Try Again",
  "emailGate.proposalLoadError": "Error loading proposal. Please try again.",

  // ── ProposalCoverScreen (screen 1) ──
  "cover.ctaLabel": "View my proposal",
  "cover.talkToConsultant": "Talk to {{name}}",
  "cover.scheduleNote.bold": "Proposal valid until {{date}}.",
  "cover.scheduleNote.rest":
    "Secure your office: book an in-person or video-call visit.",
  "cover.toggle.traditional": "Traditional lease",
  "cover.toggle.woba": "Woba",

  // ── ProposalContent ──
  "proposal.notFoundOrExpired": "Proposal not found or expired.",

  // ── Stepper / step-by-step navigation ──
  "steps.comparison": "Woba Advantage",
  "steps.spaces.singular": "Selected Space",
  "steps.spaces.plural": "Selected Spaces",
  "steps.scheduling": "Schedule Visit",
  "steps.proposal": "Make a Proposal",
  "steps.briefing": "Other options",
  "stepper.next": "Next: {{name}}",
  "nav.back": "Back",
  "nav.continue": "Continue",
  "nav.requestVisit": "Request a visit",
  "nav.backToRoomsList": "Back to the office list",

  // ── Hero copy (used by ProposalCoverScreen) ──
  "hero.subtitle":
    "Ditch the complexity of the traditional model and centralize everything in a single smart contract.",
  "hero.emailPrompt": "Your proposal is ready — enter your email to access it.",

  // ── Chat ──
  "chat.contextLabel": "Asking about",
  "chat.placeholder": "Ask about this space...",
  "chat.welcomeLine1":
    "Hello! 👋 I'm here to answer your questions about **{{spaceName}}**.",
  "chat.welcomePrice": "This space costs **R$ {{price}}/position/month**.",
  "chat.welcomeAskAnything":
    "Ask anything about this space — if I can't answer right away, we'll check with the partner and get back to you.",
  "chat.errorMessage":
    "Sorry, an error occurred while processing your message. Please try again.",
  "chat.dividerNowAbout": "Now about: {{spaceName}}",
  "chat.sendAriaLabel": "Send",
  "chat.chip.monthlyValue": "What is the monthly cost?",
  "chat.chip.parking": "Is there parking?",
  "chat.chip.internet": "What is the internet speed?",
  "chat.chip.meetingRooms": "Are meeting rooms included?",
  "chat.chip.credits": "How do Woba credits work?",
  "chat.chip.contract": "What is the contract flexibility?",
  "chat.chip.scheduleVisit": "I want to schedule a visit",

  // ── SpacesSection ──
  "spaces.selectedSpaces": "Selected Spaces",
  "spaces.optionsFor": "Options for {{companyName}}",
  "spaces.optionsForDefault": "Options for your company",
  "spaces.bestOfficesInCity":
    "We selected the best offices in {{city}} for your operation",
  "spaces.bestOptions": "We selected the best options for your operation",
  "spaces.availableOptions": "Available Options",
  "spaces.recommended": "★ Recommended",
  "spaces.combo": "Set",
  "spaces.pos": "pos",
  "spaces.scopeOffice": "Private Office",
  "spaces.scopeSpace": "Common area",
  "spaces.detail.roomId": "Room ID",
  "spaces.detail.spaceId": "Space ID",
  "spaces.detail.back": "Back",
  "spaces.detail.close": "Close",
  "spaces.detail.expandPhotos": "View all photos",
  "spaces.detail.officePhotosHeading": "{{label}} photos",
  "spaces.detail.spacePhotosHeading": "Common area photos",
  "spaces.detail.positionsHeader": "Positions",
  "spaces.detail.sizeLabel": "Size",
  "spaces.detail.pricePerPositionLabel": "Value / position",
  "spaces.detail.summary":
    "Private office to operate with {{capacity}} positions and {{size}} sqm, in the {{neighborhood}} neighborhood in {{city}}.",
  "spaces.detail.summaryNoSize":
    "Private office to operate with {{capacity}} positions, in the {{neighborhood}} neighborhood in {{city}}.",
  "spaces.detail.summaryNoNeighborhood":
    "Private office to operate with {{capacity}} positions and {{size}} sqm, in {{city}}.",
  "spaces.detail.summaryNoSizeNoNeighborhood":
    "Private office to operate with {{capacity}} positions, in {{city}}.",
  "spaces.detail.summaryComboSuffix":
    " This office is part of a set of {{total}} offices put together to meet your full request.",
  "spaces.detail.spaceSummary":
    "The space's common area has {{amenities}} amenities available.",
  "spaces.detail.aboutOfficeTitle": "About the office",
  "spaces.detail.aboutSpaceTitle": "About the common area",
  // Compliance (PED-12757): documents of the building, never of the room —
  // hence the tooltip naming the space as the subject. Positive states only.
  "spaces.detail.complianceTitle": "Compliance",
  "spaces.detail.complianceAvcb": "Fire safety certificate (AVCB)",
  "spaces.detail.complianceAvcbInfo":
    "Fire department inspection certifying the building's fire safety.",
  "spaces.detail.compliancePermit": "Operating permit",
  "spaces.detail.compliancePermitInfo":
    "City hall licence authorising the building to operate at this address.",
  "spaces.detail.amenitiesTitle": "Amenities",
  "spaces.detail.amenitiesSubtitleOffice":
    "What's available inside the private office",
  "spaces.detail.amenitiesSubtitleSpace": "What's available in the common area",
  "spaces.detail.locationTitle": "Location",
  "spaces.tabNavLabel": "Space navigation",
  "spaces.fullscreenTitle": "Spaces Location",
  "spaces.mapButton": "Show map",
  "spaces.map.pinOffices": "{{count}} offices",
  "spaces.map.showList": "Show list",
  "spaces.viewMode.cards": "View as cards",
  "spaces.viewMode.list": "View as list",
  "spaces.table.office": "Office",
  "spaces.table.monthlyValue": "Value/month",
  "spaces.table.pricePerPosition": "Cost/position",
  "spaces.table.credits": "Day passes/month",
  "spaces.table.creditBonusShort": "Earn {{credits}}",
  "spaces.mapLayers.button": "Points of interest",
  "spaces.mapLayers.viewLabel": "View",
  "spaces.mapLayers.view3d": "3D",
  "spaces.mapLayers.view2d": "2D",
  "spaces.mapLayers.poiLabel": "Points of interest",
  "spaces.card.sqm": "{{value}} sqm",
  "spaces.card.monthlyValue": "Monthly value",
  "spaces.card.creditBonus": "Earn {{credits}} day passes/month",
  "spaces.card.wantToVisit": "Select",
  "spaces.card.selected": "Selected",
  "spaces.card.visitRequested": "Visit requested",
  "spaces.card.proposalSent": "Proposal sent",
  "spaces.card.spaceHasAvcb": "Building with AVCB",
  "spaces.card.spaceHasPermit": "Building with operating permit",
  "spaces.card.complianceTooltip":
    "The building where this room is located holds this document",
  "spaces.card.viewDetails": "View details",
  "spaces.card.spacePhoto": "Common area",
  "spaces.card.comboRoomLabel": "Office {{number}}",
  "spaces.card.comboOfficesCount": "{{count}} offices",
  "spaces.card.detailsTitle": "Space details",
  "spaces.header.selectToVisit": "Choose the ideal space",
  "spaces.header.title.singular": "1 Private Office",
  "spaces.header.title.plural": "{{count}} Private Offices",
  "spaces.header.title.filteredSingular":
    "{{count}} of {{total}} Private Office",
  "spaces.header.title.filteredPlural":
    "{{count}} of {{total}} Private Offices",
  "spaces.header.lead":
    "Every option went through Woba's curation. None by chance.",
  "spaces.header.briefing.singular":
    "We chose one Private Office for your request: {{details}}.",
  "spaces.header.briefing.plural":
    "We chose a few Private Offices for your request: {{details}}.",
  "spaces.header.briefing.inCity": "in {{city}}",
  "spaces.header.briefing.positionsExact": "{{count}} positions",
  "spaces.header.briefing.contract": "{{months}}-month contract",
  "spaces.filters.sortBy": "Sort by",
  "spaces.filters.sortRecommended": "Recommended",
  "spaces.filters.sortCheapest": "Cheapest",
  "spaces.filters.sortMostPositions": "Most positions",
  "spaces.filters.capacity": "Capacity",
  "spaces.filters.priceRange": "Price range",
  "spaces.filters.size": "Size",
  "spaces.filters.capacityUpTo": "Up to {{max}} positions",
  "spaces.filters.capacityBetween": "{{min}} to {{max}} positions",
  "spaces.filters.capacityAbove": "More than {{min}} positions",
  "spaces.filters.priceUpTo": "Up to R$ {{max}}",
  "spaces.filters.priceBetween": "R$ {{min}} to R$ {{max}}",
  "spaces.filters.priceAbove": "Above R$ {{min}}",
  "spaces.filters.sizeUpTo": "Up to {{max}} sqm",
  "spaces.filters.sizeBetween": "{{min}} to {{max}} sqm",
  "spaces.filters.sizeAbove": "More than {{min}} sqm",
  "spaces.filters.noResults": "No spaces match these filters.",
  "spaces.selectedBar.hintEmpty": "Select a space to continue",
  "spaces.selectedBar.hintSelected": "Choose how you'd like to proceed",

  // ── PhotosTab ──
  "photos.prevPhoto": "Previous photo",
  "photos.nextPhoto": "Next photo",
  "photos.noImage": "No image available",
  "photos.didYouLike": "Did you like it?",
  "photos.liked": "Liked",
  "photos.disliked": "Disliked",
  "photos.positions": "positions",
  "photos.perPosition": "R$ {{price}} / position",
  "photos.priceOnRequest": "Price on request",
  "photos.licenseFee": "License Fee R$ {{price}}",
  "photos.licenseFeeIncluded": "License Fee included",

  // ── AmenitiesTab ──
  "amenities.notAvailable": "Amenity information not available.",
  "amenities.showOthers": "Others ({{count}})",
  "amenities.showLess": "Show less",

  // ── RoomEmbeddedMap (distance calculator) ──
  "map.calculateDistance": "Calculate the distance between the space and...",
  "map.placeholder": "e.g., Av. Paulista, 1000",
  "map.searching": "Searching...",
  "map.calculate": "Calculate",
  "map.distancesFrom": "Distances from",
  "map.addressNotProvided": "Address not provided",
  "map.selectedNoAddress": "Address not provided for this option",
  "map.recenter": "Recenter",
  "map.recenterTitle": "Recenter map",
  "map.clickToInteract": "Click to interact with the map",
  "map.locationNotFound":
    "We couldn't find that address. Try a different search.",
  "advantages.goToSlide": "Go to highlight {{n}}",
  "visitDialog.title": "Schedule visits",
  "visitDialog.description":
    "At this point, we would reach out to the selected Partners to confirm visit availability for their rooms.",
  "poi.noDescription": "No description",

  // ── ComparisonSection ──
  "comparison.heading": "The *smartest* way to lease and manage offices",

  // ── ComparisonSection ("Woba advantage" content) ──
  "advantage.heading": "Having an office *can be easy*.",
  "advantage.subheadingPre": "With Woba, you get access to",
  "advantage.subheadingBold": "a lot more than an office",
  "advantage.subheadingPost":
    ": everything simple, practical, and hassle-free. And you don't pay anything extra for it.",
  "advantage.cardsAriaLabel": "Woba advantages",
  "advantage.card1.title": "Best price guaranteed",
  "advantage.card1.descPre": "With Woba,",
  "advantage.card1.descBold": "pay the same monthly value",
  "advantage.card1.descPost":
    "as negotiating directly with the partner, with up to 15% negotiated off — plus the whole network.",
  "advantage.card1.sealAlt": "Best price guaranteed seal",
  "advantage.card1.tagline":
    "Found a better price, in or out of the network? We'll match it.",
  "advantage.card2.title": "Much more than an office",
  "advantage.card2.stat": "3,000+ spaces · 400+ cities",
  "advantage.card2.descPre": "Your team gets access to",
  "advantage.card2.descBold": "meeting rooms and coworking day passes",
  "advantage.card2.descPost":
    "across more than 3,000 spaces in the Woba Network, nationwide, at no extra cost.",
  "advantage.card2.badge": "Earn hours to use across the network",
  "advantage.card3.title": "Your facilities team, superpowered",
  "advantage.card3.descPre": "A team of",
  "advantage.card3.descBold": "AI agents",
  "advantage.card3.descPost":
    "handles your operation — access, bookings, maintenance and contracts — all sorted in a single invoice, no red tape.",
  "advantage.card4.title": "Office as a service",
  "advantage.card4.descBold": "The flexibility you need:",
  "advantage.card4.descPost":
    "scale up, scale down, or move cities whenever the business needs it. No penalty, no CAPEX, no construction, no long-term contract.",

  // ── CostBreakdownDrawer ("Understand the price" badge on the card) ──
  "costBreakdown.badge": "Understand the price",
  "costBreakdown.badgeWithScope": "Understand the price - {{scope}}",
  "costBreakdown.drawerTitle": "All of it, in a single invoice.",
  "costBreakdown.drawerSubtitle":
    "Your team focuses on the business, not the building.",
  "costBreakdown.compositionTitle": "How your lease works at Woba",
  "costBreakdown.compositionBodyPre":
    "This monthly price is made up of the same 3 cost groups as a traditional lease. Here, they come together as a single value, with a single due date, with no spreadsheet to reconcile.",
  "costBreakdown.compositionBodyBold": "All of it, in a single invoice.",
  "costBreakdown.totalLabel": "Monthly total",
  "costBreakdown.detailTitle": "How a traditional lease works",
  "costBreakdown.footnotePre":
    "With a traditional lease, your monthly bill splits across 3 big groups. Each of those groups has several items inside it, which adds up to {{count}} items to manage: {{count}} vendors, {{count}} contracts, each with its own due date and its own point of contact. In the end, it's your team that carries that weight every month.",
  "costBreakdown.category.rent": "Rent + Condo Fee + Property Tax",
  "costBreakdown.category.services": "Operational Services",
  "costBreakdown.category.support": "Support & Depreciation",
  "costBreakdown.item.rentBase": "Base rent",
  "costBreakdown.item.condo": "Condo fee",
  "costBreakdown.item.iptu": "Property tax",
  "costBreakdown.item.energy": "Energy",
  "costBreakdown.item.staff": "Operations staff",
  "costBreakdown.item.cleaning": "Cleaning",
  "costBreakdown.item.internet": "Internet / Telecom",
  "costBreakdown.item.maintenance": "Maintenance",
  "costBreakdown.item.water": "Water",
  "costBreakdown.item.furniture": "Furniture (depreciation)",
  "costBreakdown.item.amenities": "Coffee / Amenities",
  "costBreakdown.item.insurance": "Insurance",
  "costBreakdown.item.admin": "Admin / Overhead",

  // ── CTASection / Footer ──
  "cta.tagline": "The smartest way to hire and manage offices",
  "cta.contact": "Contact",
  "cta.proposal": "Proposal",
  "cta.validUntil": "Valid until:",
  "cta.validTBD": "To be defined",
  "cta.copyright": "© {{year}} Woba. All rights reserved.",

  // ── Logos marquee (ProposalCoverScreen footer) ──
  "logos.socialProof": "Join thousands of companies that trust Woba",

  // ── VisitSchedulingStep ──
  "schedule.overline": "Schedule your visit",
  "schedule.title": "You *choose* how you get to know it.",
  "schedule.subtitle":
    "The deeper you go, the more you understand how Woba works in practice — and how much it saves {{companyName}}.",
  "schedule.subtitleNoCompany":
    "The deeper you go, the more you understand how Woba works in practice — and how much it can save you.",
  "schedule.instruction":
    "Choose the day, time, and format of the visit for each space.",
  "schedule.dateTimeLabel": "Day and time",
  "schedule.previousDates": "Previous days",
  "schedule.nextDates": "Next days",
  "schedule.modalityLabel": "Visit format",
  "schedule.modalityInPerson": "In person",
  "schedule.modalityOnline": "Video call",
  "schedule.modalityTestDrive": "Test-drive",
  "schedule.modalityExplainerInPerson":
    "Real and up close. See the space for yourself, check out the structure up close and get a feel for your team's day-to-day there.",
  "schedule.modalityExplainerOnline":
    "Practical and immediate. Book a video call visit to see the office and ask questions directly about the space.",
  "schedule.modalityExplainerTestDrive":
    "Complete, no rush. Use the office for a full day with your team before deciding, at no cost.",
  "schedule.testDriveLearnMore": "Learn more",
  "schedule.testDriveDrawerTitle": "1-day test-drive",
  "schedule.testDriveDrawerSubtitle":
    "Complete, no rush. Use the office for a full day with your team before deciding, at no cost.*",
  "schedule.testDriveDrawerPhotoAlt":
    "Team working together in a shared Woba space",
  "schedule.testDriveHowItWorksTitle": "How it works",
  "schedule.testDriveStep1":
    "Pick an available date on the calendar — no set time, it's the whole day.",
  "schedule.testDriveStep2":
    "Woba confirms availability directly with the space.",
  "schedule.testDriveStep3":
    "You get the confirmation and bring your team on the agreed day.",
  "schedule.testDriveStep4":
    "Decide based on the real experience, not just photos.",
  "schedule.testDriveBenefitsTitle": "What you get",
  "schedule.testDriveBenefit1":
    "Test your team's day-to-day in the space before closing.",
  "schedule.testDriveBenefit2": "No cost — the visit is on Woba.",
  "schedule.testDriveBenefit3":
    "More confidence to decide, backed by real day-to-day data.",
  "schedule.testDriveClosingNote":
    "* Subject to the space's confirmation, within its business hours.",
  "schedule.testDriveNoteLabel": "Why do you want to test this space?",
  "schedule.testDriveNotePlaceholder":
    "Tell us what caught your attention in this office and what you want to confirm with the test-drive",
  "schedule.testDriveSingleRoomError":
    "Test-drive is for 1 office at a time. Remove the others to continue.",
  "schedule.visitorCountLabel": "How many people are coming?",
  "schedule.visitorCountPlaceholder": "E.g.: 4 people",
  "schedule.valueBullet1":
    "Best price guaranteed — the same as negotiating directly.",
  "schedule.valueBullet2": "Earn credits for meeting rooms and coworking.",
  "schedule.valueBullet3":
    "Flexibility to scale up, scale down, or switch cities.",
  "schedule.visitTypeInPerson": "In-person visit",
  "schedule.visitTypeOnline": "Video call visit",
  "schedule.visitTypeTestDrive": "Test-drive (1 day)",
  "schedule.slotTakenHint": "This time is already taken by another space",
  "schedule.fewSlotsAlert":
    "Few time slots left. Pick yours and lock in your visit.",
  "schedule.confirm": "Request visit",
  "schedule.subStepAriaLabel": "Scheduling steps",
  "schedule.subStepWhen": "Day and time",
  "schedule.subStepData": "Your details",
  "schedule.dataOverline": "Almost there",
  "schedule.dataTitle": "Who's *joining*?",
  "schedule.dataSubtitle": "Complete the details of who's joining the visit.",
  "schedule.hintIncomplete": "Select day, time, and format for each space",
  "schedule.hintContactIncomplete": "Fill in the fields to continue",
  "schedule.fieldEmail": "Email",
  "schedule.fieldEmailPlaceholder": "you@email.com",
  "schedule.fieldEmailError": "Enter a valid email address",
  "schedule.fieldWhatsappError": "Enter a valid phone number",
  "schedule.addParticipant": "Add another participant",
  "schedule.removeVisit": "Remove this space from the visit",
  "schedule.removeParticipant": "Remove participant",
  "schedule.participantLabel": "Participant {{n}}",
  "schedule.fieldName": "Name",
  "schedule.fieldNamePlaceholder": "Your name",
  "schedule.fieldJobTitle": "Job title",
  "schedule.fieldJobTitlePlaceholder": "Your job title",
  "schedule.fieldWhatsapp": "WhatsApp (recommended)",
  "schedule.fieldWhatsappRequired": "WhatsApp",
  "schedule.fieldWhatsappPlaceholder": "(11) 90000-0000",
  "schedule.fieldCpf": "CPF (Brazilian tax ID)",
  "schedule.fieldCpfPlaceholder": "000.000.000-00",
  "schedule.fieldCpfError": "Enter a valid CPF",
  "schedule.fieldCpfHint":
    "Used only to clear your access to the space on the test-drive day.",
  "privacy.consentPrefix": "By submitting this form, you acknowledge our",
  "privacy.consentLinkLabel": "Privacy Policy",
  "privacy.consentSuffix":
    ". Your data will be used to handle the request you made.",
  "schedule.successTitle": "Request sent!",
  "schedule.successSubtitle":
    "Your request has been sent. Keep an eye on your email — we'll let you know as soon as the partner confirms the time.",
  "schedule.successNotice":
    "Your proposal's conditions are guaranteed until {{date}}.",
  "schedule.fullAddressNotice": "Full address sent upon confirmation.",
  "schedule.postStep0Label": "Request sent",
  "schedule.postStep0Time": "Done",
  "schedule.toBeConfirmed": "to be confirmed",
  "schedule.postStep1Label": "Visit the office",
  "schedule.postStep2Label": "Final proposal",
  "schedule.postStep3Label": "Sign and move in",
  "schedule.postStep1Time": "From 48h",
  "schedule.postStep2Time": "48h after the visit",
  "schedule.postStep3Time": "Up to 15 days",
  "schedule.confirmationOverline": "Next steps",
  "schedule.confirmationTitle": "From interest to move-in, *in just days*.",
  "schedule.confirmationSubtitle":
    "Follow what happens next until you move into the new office.",
  "schedule.recapOverline": "Woba Network",
  "schedule.recapHeading": "You hire an office.\nYou get an entire country.",
  "schedule.recapSubheading":
    "5% of your contract value becomes a bonus in credits, so your team isn't tied to a single address. They can use meeting rooms, workstations and event spaces in any city in Brazil.",
  "schedule.recapPricePre": "And the price?",
  "schedule.recapPriceBold": "Always the same as negotiating directly",
  "schedule.recapPricePost": "— at no extra cost.",
  "schedule.recapStat1Value": "+3,000",
  "schedule.recapStat1Label": "flexible offices",
  "schedule.recapStat2Value": "+4,200",
  "schedule.recapStat2Label": "meeting rooms",
  "schedule.recapStat3Value": "+1,000",
  "schedule.recapStat3Label": "event spaces",
  "schedule.recapStat4Value": "+400",
  "schedule.recapStat4Label": "cities in LatAm",
  "schedule.recapType1Title": "Workstations",
  "schedule.recapType1DescPre": "1 day pass at any of the",
  "schedule.recapType1DescCount": "+23,000",
  "schedule.recapType1DescPost": "workstations",
  "schedule.recapType2Title": "Meeting Rooms",
  "schedule.recapType2DescPre": "1 hour at any of the",
  "schedule.recapType2DescCount": "+4,200",
  "schedule.recapType2DescPost": "rooms",
  "schedule.recapType3Title": "Event Spaces",
  "schedule.recapType3DescPre": "1 day pass at any of the",
  "schedule.recapType3DescCount": "+1,000",
  "schedule.recapType3DescPost": "spaces",
  "schedule.recapType4Title": "Private Offices",
  "schedule.recapType4DescPre": "1 month at any of the",
  "schedule.recapType4DescCount": "+5,200",
  "schedule.recapType4DescPost": "offices",
  "schedule.recapCredits": "credits",
  "schedule.recapCreditsPerPerson": "credits/person",
  "schedule.recapCreditsPerPosition": "credits/position",

  // ── Spaces (additional) ──
  "spaces.option": "Private Office {{number}}",
  "spaces.ofTotal": "of {{total}}",

  // ── POI Categories ──
  "poi.restaurant": "Restaurants",
  "poi.cafe": "Cafés",
  "poi.pharmacy": "Pharmacies",
  "poi.transport": "Transport",
  "poi.hotel": "Hotels",
  "poi.parking": "Parking",

  // ── Chat (additional) ──
  "chat.defaultSpaceName": "this space",

  // ── CreditInfoDrawer ──
  "creditDrawer.viewDetails": "View credit details",
  "creditDrawer.title": "Credits included in your contract",
  "creditDrawer.subtitle":
    "5% of your contract value becomes a bonus in credits.",
  "creditDrawer.creditsLabel": "Credits/month included*",
  "creditDrawer.creditsValue": "+{{credits}} credits",
  "creditDrawer.creditsFootnote":
    "* Credits aren't cumulative: anything unused in the month doesn't carry over to the next one.",
  "creditDrawer.howItWorksBody":
    "Your team isn't tied to this address: with the included credits, they can use meeting rooms, workstations and event spaces in any city in Brazil — without paying anything extra for it.",
  "creditDrawer.formatMeetingRoomTitle": "Meeting rooms",
  "creditDrawer.formatMeetingRoomBody":
    "{{credits}} credits are worth {{hours}}h of meeting room time for up to 4 people — this changes with room size.",
  "creditDrawer.formatCoworkingTitle": "Coworking (shared workstations)",
  "creditDrawer.formatCoworkingBody":
    "{{credits}} credits are worth {{credits}} days of access to shared spaces.",
  "creditDrawer.formatPrivateOfficeTitle": "Private offices by the day",
  "creditDrawer.formatPrivateOfficeBody":
    "Use credits to set up temporary spaces for projects or teams.",
  "creditDrawer.formatEventSpaceTitle": "Event and training spaces",
  "creditDrawer.formatEventSpaceBody":
    "Book larger rooms and auditoriums using multiple credits.",
  "creditDrawer.quadroHoursLabel": "per month",
  "creditDrawer.quadroDaysLabel": "days/month",
  "creditDrawer.onRequest": "On request",
  "creditDrawer.practiceTitle": "In practice",
  "creditDrawer.practiceDayToDayTitle": "How this applies to your day-to-day",
  "creditDrawer.practiceBullet1": "Your office stops being a single address",
  "creditDrawer.practiceBullet2":
    "You pick the right format for each moment, no new contract needed",
  "creditDrawer.practiceBullet3":
    "The credit is already included — no extra cost on your invoice",
  "creditDrawer.practiceCta":
    "Instead of contracting more space, you use credits when you need them.",

  // ── RoomPriceCard (price card in the room details drawer, desktop) ──
  "priceCard.monthlyLabel": "Monthly office fee",
  "priceCard.monthlyLabelWithScope": "Monthly fee - {{scope}}",
  "priceCard.perPositionValue": "{{price}} / position",

  // ── CreditInfoDrawer, "extra credits" variant (ENT, PED-12653) ──
  "extraCreditDrawer.viewDetails": "View extra credit details",
  "extraCreditDrawer.title": "Extra credits in your contract",
  "extraCreditDrawer.subtitle":
    "An optional credit package, on top of the included ones, to use across the Woba network.",
  "extraCreditDrawer.creditsLabel": "Extra credits/month*",
  "extraCreditDrawer.monthlyValueLabel": "Package value/month",
  "extraCreditDrawer.howItWorksBody":
    "On top of the included credits, this package gives your team more credits per month for meeting rooms, workstations and event spaces in any city in Brazil — and it is part of your monthly total.",
  "extraCreditDrawer.practiceBullet3":
    "The package is optional and joins your monthly total — no separate contract",

  // ── PriceGuaranteeModal ──
  "priceGuarantee.title": "Best price guarantee",
  "priceGuarantee.subtext": "Same price as the partner, up to 15% off",
  "priceGuarantee.bodyPre":
    "We negotiate directly with the partner to get you the best price on your proposal. We get you ",
  "priceGuarantee.bodyBold": "up to 15% off",
  "priceGuarantee.bodyPost": " the office's value.",
  "priceCard.creditsLabel": "Credits included",
  "priceCard.freeBadge": "free",
  "priceCard.creditsSubtext":
    "Equivalent to {{credits}} day passes in shared spaces/month",
  "priceCard.flexCreditsLabel": "Extra credits (optional)",
  "priceCard.setupFeeLabel": "Setup Fee",
  "priceCard.setupFeeCaption": "Paid once",
  "priceCard.licenseFeeLabel": "License Fee",
  "priceCard.licenseFeeSemiannualCaption": "Paid semiannually",
  "priceCard.licenseFeeAnnualCaption": "Paid annually",
  "priceCard.helpLink": "Still have questions? Talk to us",
  "priceCard.scheduleVisit": "Request a visit",
  "priceCard.makeProposal": "Make a proposal",

  // ── ProposalMakingStep ("Make a proposal" flow) ──
  "proposalMaking.hub.overline": "Make your proposal",
  "proposalMaking.hub.headline": "You *decide* how to move forward.",
  "proposalMaking.hub.subtitle":
    "Review the pricing and room summary before choosing the next steps for {{companyName}}.",
  "proposalMaking.hub.subtitleNoCompany":
    "Review the pricing and room summary before choosing the next steps.",
  "proposalMaking.subStepDefine": "Proposal terms",
  "proposalMaking.subStepAdjust": "Price and space adjustments",
  "proposalMaking.subStepReview": "Identification",
  "proposalMaking.subStepAriaLabel": "Proposal steps",
  "proposalMaking.choose.pickerLabel": "Choose an option",
  "proposalMaking.choose.acceptLabel": "Accept conditions",
  "proposalMaking.choose.acceptDescription":
    "Accepting the current conditions speeds up the review and puts your proposal ahead of ones still being negotiated.",
  "proposalMaking.choose.negotiateLabel":
    "Negotiate value or request an adjustment",
  "proposalMaking.choose.negotiateDescription":
    "Propose a different value or request adjustments to the space — our team reviews and replies within 2 business days.",
  "proposalMaking.negotiate.overline": "Negotiate value",
  "proposalMaking.negotiate.headline": "Propose the *right* value for you.",
  "proposalMaking.negotiate.subtitle":
    "Adjust the proposed monthly value and tell our team why it works for you — the closer to the current value, the higher the chance of a quick acceptance from the partner.",
  "proposalMaking.negotiate.subtitleNoPrice":
    "Tell our team what you'd like to propose and why it works for you — they'll review it with the partner and get back to you.",
  "proposalMaking.negotiate.sectionTitle": "Price adjustment",
  "proposalMaking.negotiate.fieldLabel": "Your proposal",
  "proposalMaking.negotiate.fieldPlaceholder": "0",
  "proposalMaking.negotiate.goodProposalTitle": "Great proposal!",
  "proposalMaking.negotiate.goodProposalDescription":
    "A value at or above the current one has a high chance of being accepted.",
  "proposalMaking.negotiate.moderateDiscountTitle": "Good chance of acceptance",
  "proposalMaking.negotiate.moderateDiscountDescription":
    "Discounts of up to 15% have a good chance of being approved by the partner.",
  "proposalMaking.negotiate.overLimitTitle": "Discount above the limit",
  "proposalMaking.negotiate.overLimitDescription":
    "We can send proposals with up to {{percent}}% off. Adjust it to {{minimum}} or more to continue.",
  "proposalMaking.negotiate.overLimitHint":
    "Adjust the proposal to {{minimum}} or more to continue",
  "proposalMaking.negotiate.discountBadge": "-{{percent}}%",
  "proposalMaking.negotiate.increaseBadge": "+{{percent}}%",
  "proposalMaking.negotiate.reasonLabel":
    "Tell us the reason for your proposal",
  "proposalMaking.negotiate.reasonPlaceholder":
    "Tell us the reason for the value adjustment...",
  "proposalMaking.negotiate.flexCreditsLabel": "Extra credits per month",
  "proposalMaking.negotiate.flexCreditsUnit": "credits",
  "proposalMaking.negotiate.flexCreditsHelper":
    "Comes to {{value}}/month. The negotiation above applies to the monthly rent only.",
  "proposalMaking.adjustmentsChoice.pickerLabel": "Space setup adjustment",
  "proposalMaking.adjustmentsChoice.overline": "Room adjustments",
  "proposalMaking.adjustmentsChoice.headline": "Need any *adjustments*?",
  "proposalMaking.adjustmentsChoice.subtitle":
    "You can ask to remove, install, repair or replace an item.",
  "proposalMaking.adjustmentsChoice.noneLabel": "No adjustments",
  "proposalMaking.adjustmentsChoice.noneBadge": "Higher chance of approval",
  "proposalMaking.adjustmentsChoice.someLabel": "With adjustments",
  "proposalMaking.adjustmentsChoice.someBadge":
    "Subject to the space's evaluation",
  "proposalMaking.adjustmentType.overline": "Adjustment type",
  "proposalMaking.adjustmentType.headline": "What type of *adjustment*?",
  "proposalMaking.adjustmentType.subtitle":
    "You can add one adjustment at a time.",
  "proposalMaking.adjustmentType.layout": "Layout",
  "proposalMaking.adjustmentType.layoutDescription":
    "Remove or install an item, or change/replace something in the space.",
  "proposalMaking.adjustmentType.maintenance": "Maintenance or repair",
  "proposalMaking.adjustmentType.maintenanceDescription":
    "Fix or perform maintenance on something that already exists.",
  "proposalMaking.adjustmentType.positions": "Positions",
  "proposalMaking.adjustmentType.positionsDescription":
    "Increase or decrease the office's number of positions.",
  "proposalMaking.adjustmentDescription.overline": "Adjustment details",
  "proposalMaking.adjustmentDescription.headline": "Describe what you *need*.",
  "proposalMaking.adjustmentDescription.subtitle":
    "The more detail, the faster we can review your request.",
  "proposalMaking.adjustmentDescription.placeholder":
    "Describe what you need...",
  "proposalMaking.adjustmentDescription.continueLabel": "Review",
  "proposalMaking.review.overline": "Almost there",
  "proposalMaking.review.headline": "Review your *proposal*.",
  "proposalMaking.review.subtitle":
    "Confirm the pricing and adjustments before sending it to our team.",
  "proposalMaking.review.bannerAccepted": "This is a great proposal!",
  "proposalMaking.review.bannerNegotiating":
    "Your proposal will be sent for our partner's review.",
  "proposalMaking.review.adjustmentsTitle": "Requested adjustments",
  "proposalMaking.review.addAdjustment": "Add adjustment",
  "proposalMaking.review.removeAdjustment": "Remove",
  "proposalMaking.review.identificationTitle": "Your details",
  "proposalMaking.review.occupancyDateLabel": "Expected occupancy date",
  "proposalMaking.review.contractTermLabel": "Contract term",
  "proposalMaking.review.contractTermOption.12": "12 months",
  "proposalMaking.review.contractTermOption.24": "24 months",
  "proposalMaking.review.contractTermOption.36": "36 months",
  "proposalMaking.review.contractTermOption.48": "48 months",
  "proposalMaking.review.contractTermOption.other": "Other",
  "proposalMaking.review.contractTermCustomPlaceholder": "How many months?",
  "proposalMaking.review.submitLabel": "Confirm and send",
  "proposalMaking.sent.overline": "Next steps",
  "proposalMaking.sent.headline": "Proposal sent!",
  "proposalMaking.sent.subtitle": "Your proposal was sent to the Woba team.",
  "proposalMaking.sent.notice": "This proposal is valid until {{date}}.",
  "proposalMaking.sent.roomStatusLabel": "Proposal sent",
  "proposalMaking.sent.postStep0Label": "Proposal sent",
  "proposalMaking.sent.postStep0Time": "Done",
  "proposalMaking.sent.postStep1Label": "Under review",
  "proposalMaking.sent.postStep1Time": "Response within 2 business days",
  "proposalMaking.sent.postStep2Label": "Approval",
  "proposalMaking.sent.postStep2Time": "After partner's response",
  "proposalMaking.sent.postStep3Label": "Sign and move in",
  "proposalMaking.sent.postStep3Time": "Up to 15 days",

  // ── In-proposal deep briefing (PED-14146) ──
  "briefing.card.title": "We can go beyond these options",
  "briefing.card.subtitle": "Tell us what you need. We'll handle the rest:",
  "briefing.card.cta": "Tell us what you need",
  "briefing.card.smallTeam.bullet1": "You tell us what didn't work",
  "briefing.card.smallTeam.bullet2": "We bring new options",
  "briefing.card.smallTeam.bullet3": "Or adjust the room you picked",
  "briefing.card.smallTeam.bullet4": "No starting over",
  "briefing.card.largeTeam.bullet1": "More options in our network",
  "briefing.card.largeTeam.bullet2": "Adapted before you move in",
  "briefing.card.largeTeam.bullet3": "Or designed from scratch",
  "briefing.card.largeTeam.bullet4": "We handle the build",

  "briefing.stepper.ariaLabel": "Form steps",
  "briefing.stepper.adjust": "Adjustments",
  "briefing.stepper.intent": "What you're after",
  "briefing.stepper.space": "The space",
  "briefing.stepper.contact": "Your details",
  "briefing.stage.overline": "Step {{number}} of {{total}}",
  "briefing.submitLabel": "Send",

  "briefing.adjust.title": "Let's *adjust* the room you liked",
  "briefing.adjust.subtitle":
    "Only details of the room were off. Tell us which one you liked and what you'd change.",
  "briefing.adjust.spaceLabel": "The room you liked best",
  "briefing.adjust.spacePlaceholder": "Pick one of the proposal's options",
  "briefing.adjust.label":
    "You liked this room, but would change something to make it ideal for your team? Tell us.",
  "briefing.adjust.groupStructural": "Changes to the structure",
  "briefing.adjust.structuralHelp":
    "Structural changes make this a customized project: timeline and investment change, and a dedicated team takes over your request.",
  "briefing.adjust.simpleNotesLabel": "Want to detail these adjustments?",
  "briefing.adjust.simpleNotesPlaceholder":
    "E.g. swap the desks for 4-seat workstations and turn the layout toward the window.",
  "briefing.adjust.structuralNotesLabel": "Want to detail these changes?",
  "briefing.adjust.structuralNotesPlaceholder":
    "E.g. close off the back room with a glass partition to make it a meeting room.",
  "briefing.adjust.notesLabel": "Anything else you'd change?",
  "briefing.adjust.notesPlaceholder":
    "E.g. I'd swap the desks for 4-seat workstations.",

  "briefing.stepper.feedback": "The options",
  "briefing.feedback.title": "What did you think of the *options*?",
  "briefing.feedback.subtitle":
    "Tell us what worked and what didn't in each one. That's what makes the next search sharper.",
  "briefing.feedback.cardRooms": "The options you saw",
  "briefing.feedback.help":
    "Give a thumb to at least one option. Anything you already rated in the listing shows up here already marked.",
  "briefing.feedback.likedLabel": "What you liked",
  "briefing.feedback.dislikedLabel": "What didn't work",
  "briefing.feedback.missedLabel": "What was missing",
  "briefing.feedback.missedHelp":
    "In your own words, what kept you from going ahead with any of them.",
  "briefing.feedback.missedPlaceholder":
    "E.g. the area is good, but they were all far from the subway.",
  "briefing.feedback.needLabel": "What it needs to have",
  "briefing.feedback.needHelp":
    "What the next option can't do without. One item per line.",
  "briefing.feedback.needPlaceholder":
    "E.g. meeting room for 10 people\nEx.: space for the sales team to take calls",
  "briefing.feedback.cardSearch": "The new search",
  "briefing.section.feedback": "New search",
  "briefing.hint.feedback":
    "Rate at least one option and fill in the fields marked with *.",

  "briefing.intent.title": "What are you *after*?",
  "briefing.intent.subtitle":
    "Your answer decides what we show you and who on our team takes the request.",
  "briefing.intent.label": "Pick one option",
  "briefing.intent.option.readyOffices.title": "Other move-in-ready offices",
  "briefing.intent.option.readyOffices.description":
    "We look for new options in the Woba inventory, same format as the ones you saw.",
  "briefing.intent.option.customizable.title":
    "Customize one of the offices you saw",
  "briefing.intent.option.customizable.description":
    "Pick one of the options in your proposal and tell us what you'd change: layout, furniture, branding or the structure itself.",
  "briefing.intent.option.tailorMade.title":
    "An exclusive space, built to measure",
  "briefing.intent.option.tailorMade.description":
    "We find the property and design the office from scratch for your team. Longer timeline, higher investment.",
  // Aviso de preço sob Personalizável e Sob Medida (pedido do Growth).
  "briefing.priceNotice.customizable":
    "A customized office costs about 20% to 40% more than a ready-to-use office.",
  "briefing.priceNotice.tailorMade":
    "A tailor-made office costs about 20% to 40% more than a ready-to-use office.",
  "briefing.priceNotice.accept": "I understand and want to continue",

  "briefing.space.tailorMade.title": "What's the *space* you have in mind?",
  "briefing.space.tailorMade.subtitle":
    "We already have the area and budget from your proposal. Now tell us what the property has to be like — this is what the project is drawn from.",
  "briefing.space.cardBuilding": "The property",

  "briefing.field.region.label": "City and area / neighborhood",
  "briefing.field.region.placeholder":
    "E.g. São Paulo — Pinheiros or Faria Lima",
  "briefing.field.region.help":
    "We start from your proposal's area. Change it to where you want to search now.",
  "briefing.field.people.label": "Number of people",
  "briefing.field.people.placeholder": "E.g. 24",
  "briefing.field.people.help":
    "From your proposal. Change it if the number is different.",
  "briefing.field.deadline.label": "Move-in timeline",
  "briefing.field.budget.label": "Monthly budget",
  "briefing.field.budget.placeholder": "E.g. up to R$ 30,000 a month",
  "briefing.field.budget.help":
    "From your proposal. Optional, but it helps us only bring what fits.",
  "briefing.field.mustHave.label": "What can't be missing",
  "briefing.field.mustHave.placeholder":
    "E.g. meeting room for 10 people\nE.g. space for the sales team to take calls",
  "briefing.field.mustHave.help": "One item per line.",
  "briefing.field.sizeM2.label": "Approximate size (m²)",
  "briefing.field.sizeM2.placeholder": "E.g. 300",
  "briefing.field.buildingType.label": "Property type",
  "briefing.field.standard.label": "Standard you want",
  "briefing.field.nearby.label": "Must-have basic amenities",
  "briefing.field.priorities.label": "Your three biggest priorities",
  "briefing.field.priorities.help":
    "In order: what you look at first when comparing two spaces.",
  "briefing.field.priority1.placeholder": "1st priority",
  "briefing.field.priority2.placeholder": "2nd priority",
  "briefing.field.priority3.placeholder": "3rd priority",
  "briefing.field.adjustments.label": "Changes you'd make to the space",
  "briefing.field.likedSpace.label": "Room the client liked",
  "briefing.field.missedNotes.label": "Details on what was missing",
  "briefing.field.adjustmentNotes.label": "Details on the changes",
  "briefing.field.intent.label": "What they're after",
  "briefing.field.name.label": "Name",
  "briefing.field.jobTitle.label": "Job title",
  "briefing.field.email.label": "Email",
  "briefing.field.whatsapp.label": "WhatsApp",

  "briefing.option.adjustments.furnitureLayout": "Change the furniture layout",
  "briefing.option.adjustments.furnitureSwap": "Swap or add furniture",
  "briefing.option.adjustments.deskCount":
    "More or fewer desks in the same room",
  "briefing.option.adjustments.maintenance":
    "Fix or service something that's already there",
  "briefing.option.adjustments.storage": "Lockers / storage space",
  "briefing.option.adjustments.branding": "Brand the space as the company",
  "briefing.option.adjustments.otherRoomSameBuilding":
    "See another room in the same building",
  "briefing.option.adjustments.walls": "Take down or put up a wall",
  "briefing.option.adjustments.internalMeetingRoom":
    "Add a meeting room or booth inside the space",
  "briefing.option.adjustments.flooringCeiling": "Change flooring or ceiling",
  "briefing.option.adjustments.installations":
    "Change HVAC, electrical or plumbing",
  "briefing.option.adjustments.mergeRooms": "Merge two rooms",
  "briefing.option.intent.readyOffices": "Other move-in-ready offices",
  "briefing.option.intent.customizable":
    "Customize one of the offices they saw",
  "briefing.option.intent.tailorMade": "An exclusive space, built to measure",
  "briefing.option.deadline.upTo30Days": "Within 30 days",
  "briefing.option.deadline.upTo3Months": "In 1 to 3 months",
  "briefing.option.deadline.upTo6Months": "In 3 to 6 months",
  "briefing.option.deadline.over6Months": "More than 6 months",
  "briefing.option.deadline.noDate": "No date yet",
  "briefing.option.buildingType.corporateSlab": "Floor in a corporate building",
  "briefing.option.buildingType.streetBuilding":
    "House or street-level property",
  "briefing.option.buildingType.either": "Either works",
  "briefing.option.standard.premium": "Premium (AAA)",
  "briefing.option.standard.intermediate": "Mid-range",
  "briefing.option.standard.simple": "Simpler",
  "briefing.option.nearby.kitchen": "Kitchen or canteen",
  "briefing.option.nearby.buildingReception": "Reception in the building",
  "briefing.option.nearby.publicTransport": "Public transit nearby",
  "briefing.option.nearby.restaurants": "Restaurants nearby",
  "briefing.option.nearby.parking": "Parking",

  "briefing.stepper.routine": "Team and priorities",
  "briefing.routine.title": "How will your team *use* the space?",
  "briefing.routine.subtitle":
    "The day to day, what can't be missing, and references that help us get it right faster.",
  "briefing.routine.cardMeetingRooms": "Meeting rooms",
  "briefing.routine.cardPriorities": "What matters most",
  "briefing.routine.cardReferences": "References",
  "briefing.space.cardSize": "Size and timeline",
  "briefing.field.investment.label":
    "To have the space your way, you'd invest:",
  "briefing.option.investment.sameValue": "The same as the proposal",
  "briefing.option.investment.aLittleMore": "A little more",
  "briefing.option.investment.whateverItTakes":
    "Whatever it takes to get the space right",
  "briefing.field.nearbyScope.label": "Can we also look in nearby areas?",
  "briefing.option.nearbyScope.sameRegion": "Same area only",
  "briefing.option.nearbyScope.sameAndNearby": "Same area and nearby ones",
  "briefing.field.parking.label": "Parking",
  "briefing.field.parking.placeholder": "How many spots do you need? E.g. 5",
  "briefing.field.meetingRooms.placeholder": "How many?",
  "briefing.field.meetingRoomsSmall.label": "Small (up to 4 people)",
  "briefing.field.meetingRoomsMedium.label": "Medium (6 to 8 people)",
  "briefing.field.meetingRoomsLarge.label": "Large (10 or more)",
  "briefing.field.niceToHave.label": "Nice to have",
  "briefing.field.niceToHave.placeholder":
    "E.g. bike rack, gym in the building, terrace",
  "briefing.field.likedReference.label":
    "Any office or building you've seen and liked? Tell us what stood out: address, building, floor plan, price.",
  "briefing.field.likedReference.placeholder":
    "E.g. building X on Faria Lima, for the location and the floor plan",
  "briefing.field.discardedReference.label": "Any you ruled out? Why?",
  "briefing.field.discardedReference.placeholder":
    "E.g. building Y, because it had no parking",
  "briefing.section.routine": "Day to day, priorities and references",
  "briefing.hint.routine":
    "Check at least one amenity and fill in the 1st priority.",

  "briefing.contact.title": "Your *details*",
  "briefing.contact.subtitle": "Who we should get back to with the options.",

  "briefing.hint.adjust": "Pick the room and at least one change.",
  "briefing.hint.intent": "Pick one of the options.",
  "briefing.hint.priceNotice": "Confirm the price notice to continue.",
  "briefing.hint.space":
    "Enter the timeline and standard — or, to adjust, the room and the changes.",
  "briefing.hint.contact": "Fill in your details to send.",

  "briefing.section.adjust": "Adjustments to the chosen room",
  "briefing.section.intent": "What you're after",
  "briefing.section.space": "The space you want",
  "briefing.section.contact": "Your details",

  "briefing.sent.overline": "Request sent",
  "briefing.sent.headline": "Answers *sent*!",
  "briefing.sent.subtitleReadySearch":
    "Bruna will look for new options in our inventory, with your ratings in hand, and show you what she finds.",
  "briefing.sent.subtitleReady":
    "Our team will look for new options based on your answers and come back with the alternatives.",
  "briefing.sent.subtitleCustomizable":
    "Your request involves changes to the space, so it went to the customized offices team. They'll come back with what's possible, the timeline and the investment.",
  "briefing.sent.subtitleTailorMade":
    "Our team will use your answers to find the property and design your team's office. You'll receive a tailor-made proposal.",
  "briefing.sent.summaryLabel": "Summary of what you sent",
  "briefing.sent.postStep0Label": "Answers sent",
  "briefing.sent.postStep0Time": "Done",
  "briefing.sent.postStep1LabelReady": "Search for new options",
  "briefing.sent.postStep1TimeReady": "Within 2 business days",
  "briefing.sent.postStep1LabelBuild": "Review of your request",
  "briefing.sent.postStep1TimeBuild": "Within 7 days",
  "briefing.sent.postStep2LabelReady": "New options in the proposal",
  "briefing.sent.postStep2TimeReady": "After the search",
  "briefing.sent.postStep2LabelBuild": "Proposal with timeline and investment",
  "briefing.sent.postStep2TimeBuild": "After the review",
  "briefing.sent.postStep3Label": "Sign and move in",
  "briefing.sent.postStep3TimeReady": "Within 15 days",
  "briefing.sent.postStep3TimeBuild": "Timeline to be agreed",

  // ── Rating (recommendation feedback) ──
  "rating.prompt": "Did we get it right?",
  "rating.drawerPrompt": "Did we get this office right?",
  "rating.thumbUp": "I liked this office",
  "rating.thumbDown": "I didn't like this office",
  "rating.modalTitlePositive": "Great! We got the recommendation right",
  "rating.modalTitleNegative": "We didn't quite get the recommendation right",
  "rating.modalSupportPositive":
    "Your feedback helps us understand what worked for you.",
  "rating.modalSupportNegative":
    "We have more options for you. Your feedback helps us bring the most accurate ones.",
  "rating.questionNegative": "What didn't fit?",
  "rating.questionPositive": "What did you like the most?",
  "rating.subReasonsLabel": "What's missing?",
  "rating.commentLabel": "Want to tell us more? (optional)",
  "rating.commentPlaceholder": "Write here...",
  "rating.submit": "Send feedback",
  "rating.confirmationTitle": "Got it!",
  "rating.confirmationBody":
    "We'll use your feedback to improve the next suggestions.",
  "rating.close": "Close",
  "rating.reason.photos": "The photos don't help me understand the office",
  "rating.reason.capacity": "It doesn't fit the number of people I need",
  "rating.reason.location": "The area doesn't work for me",
  "rating.reason.price": "Not worth the asking price",
  "rating.reason.layoutCondition":
    "The layout or condition of the office didn't appeal to me",
  "rating.reason.missingRequirements": "It's missing something I need",
  "rating.reason.capacityFit": "Fits my team well",
  "rating.reason.locationGreat": "The area is great for me",
  "rating.reason.visualAppeal": "I liked the look of the office",
  "rating.reason.amenitiesComplete": "It has everything I need",
  "rating.reason.priceValue": "Good value for money",
  "rating.subReason.amenities": "Amenities",
  "rating.subReason.equipment": "Equipment",
  "rating.subReason.accessibility": "Accessibility",
  "rating.subReason.other": "Other features",
};
